import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Section } from "../components/AtlasUI";
import { chatCompletion } from "../services/ai";
import { getDeals } from "../mock";
import { Search, Plus, Trash2, Sparkles, Globe, Building2, TrendingUp, FileText, Target } from "lucide-react";

const STORAGE_KEY = "atlas_research_targets";

function getTargets() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
}
function saveTargets(arr) { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); }

const RESEARCH_TYPES = ["General", "Competitor"];
const STATUSES = ["Active", "Archived", "Converted"];
const SECTORS = ["SaaS", "Fintech", "Healthcare", "Industrial", "Energy", "Consumer", "Logistics", "EdTech", "Other"];

const STATUS_COLORS = {
  Active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Archived: "bg-gray-100 text-gray-600 border-gray-200",
  Converted: "bg-blue-100 text-blue-800 border-blue-200",
};

const TYPE_COLORS = {
  General: "bg-purple-100 text-purple-800 border-purple-200",
  Competitor: "bg-orange-100 text-orange-800 border-orange-200",
};

// Hook to listen for team changes
function useTeamChange() {
  const [teamKey, setTeamKey] = useState(0);
  useEffect(() => {
    const handler = () => setTeamKey(k => k + 1);
    window.addEventListener("atlas_team_changed", handler);
    return () => window.removeEventListener("atlas_team_changed", handler);
  }, []);
  return teamKey;
}

// Deal Scoring Component
function DealScoring() {
  const nav = useNavigate();
  const teamKey = useTeamChange();
  const deals = useMemo(() => getDeals().slice(0, 15).sort((a, b) => b.score - a.score), [teamKey]);
  const [explainMap, setExplainMap] = useState({});

  const explainScore = async (d) => {
    setExplainMap(prev => ({ ...prev, [d.id]: { loading: true, text: prev[d.id]?.text || "" } }));
    try {
      const { content } = await chatCompletion(
        `Explain score ${d.score} for ${d.name} (sector ${d.sector}, EV ${d.enterpriseValue}M, stage ${d.stage}, geography ${d.geography || "MEA"}). 3 bullets + 1 risk.`,
        { systemPrompt: "You are an M&A analyst at GII. Be specific and concise." }
      );
      setExplainMap(prev => ({ ...prev, [d.id]: { loading: false, text: content } }));
    } catch (e) { setExplainMap(prev => ({ ...prev, [d.id]: { loading: false, text: `Error: ${e.message}` } })); }
  };

  const scoreColor = (s) => s >= 85 ? "bg-emerald-100 text-emerald-700" : s >= 70 ? "bg-blue-100 text-atlas-accent" : "bg-gray-100 text-gray-600";

  return (
    <Card className="border border-gray-100 shadow-sm">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-3 font-semibold text-atlas-description text-xs uppercase tracking-wide">Company</th>
                <th className="text-left px-4 py-3 font-semibold text-atlas-description text-xs uppercase tracking-wide">Sector</th>
                <th className="text-left px-4 py-3 font-semibold text-atlas-description text-xs uppercase tracking-wide">Geography</th>
                <th className="text-left px-4 py-3 font-semibold text-atlas-description text-xs uppercase tracking-wide">Stage</th>
                <th className="text-left px-4 py-3 font-semibold text-atlas-description text-xs uppercase tracking-wide">Score</th>
                <th className="text-left px-4 py-3 font-semibold text-atlas-description text-xs uppercase tracking-wide">EV ($M)</th>
                <th className="text-left px-4 py-3 font-semibold text-atlas-description text-xs uppercase tracking-wide">EBITDA %</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {deals.map(d => (
                <React.Fragment key={d.id}>
                  <tr className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <button onClick={() => nav(`/deal/${d.id}`)} className="font-semibold text-atlas-heading hover:text-atlas-accent hover:underline text-left">{d.name}</button>
                      {d.description && <div className="text-xs text-atlas-description mt-0.5 max-w-xs truncate">{d.description}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-atlas-accent">{d.sector}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-atlas-description">{d.geography || "MEA"}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{d.stage}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-100 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full bg-atlas-accent" style={{ width: `${d.score}%` }} />
                        </div>
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${scoreColor(d.score)}`}>{d.score}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-atlas-heading">{d.enterpriseValue}</td>
                    <td className="px-4 py-3 text-sm text-atlas-description">{d.ebitdaMargin}%</td>
                    <td className="px-4 py-3">
                      <Button variant="outline" className="h-7 text-xs border-gray-200" onClick={() => explainScore(d)}>
                        {explainMap[d.id]?.loading ? "…" : "Explain"}
                      </Button>
                    </td>
                  </tr>
                  {explainMap[d.id]?.text && (
                    <tr className="bg-blue-50">
                      <td colSpan={8} className="px-4 py-3">
                        <div className="text-xs text-atlas-heading whitespace-pre-wrap leading-relaxed">{explainMap[d.id].text}</div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ResearchNotes({ target, onClose }) {
  const NOTES_KEY = `atlas_research_notes_${target.id}`;
  const [notes, setNotes] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NOTES_KEY) || "[]"); } catch { return []; }
  });
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("General");
  const [aiAnalysis, setAiAnalysis] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => { localStorage.setItem(NOTES_KEY, JSON.stringify(notes)); }, [notes, NOTES_KEY]);

  const addNote = () => {
    if (!content.trim()) return;
    const note = { id: crypto.randomUUID(), content, category, created_at: Date.now() };
    setNotes(prev => [note, ...prev]);
    setContent("");
  };

  const runAI = async () => {
    setAiBusy(true);
    setAiAnalysis("");
    try {
      const prompt = target.type === "Competitor"
        ? `Competitor analysis for ${target.name} (sector: ${target.sector || "N/A"}). Provide: 1) 3-sentence summary, 2) 4 key market position findings, 3) 3 risks to our thesis, 4) 3 opportunities revealed. Be specific and actionable.`
        : `General research brief for ${target.name} (sector: ${target.sector || "N/A"}). Provide: 1) 3-sentence executive summary, 2) 4 key findings, 3) 3 key risks, 4) 3 investment opportunities. Be specific.`;
      const { content: res } = await chatCompletion(prompt, { systemPrompt: "You are a PE research analyst. Be concise, specific, and PE-focused." });
      setAiAnalysis(res || "");
    } catch (e) {
      setAiAnalysis(`Error: ${e.message}`);
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-lg font-semibold text-atlas-heading">{target.name}</h3>
        <Badge className={`text-xs border ${TYPE_COLORS[target.type]}`}>{target.type}</Badge>
        {target.sector && <Badge className="text-xs bg-gray-100 text-gray-600 border-gray-200">{target.sector}</Badge>}
      </div>
      {target.description && <p className="text-sm text-atlas-description">{target.description}</p>}

      <div className="flex gap-2">
        <Button
          onClick={runAI}
          disabled={aiBusy}
          className="bg-atlas-accent hover:bg-blue-700 text-white text-sm"
        >
          <Sparkles size={14} className="mr-1" />
          {aiBusy ? "Analyzing…" : "AI Research Brief"}
        </Button>
      </div>

      {aiAnalysis && (
        <Card className="border border-blue-100 bg-blue-50">
          <CardContent className="p-4">
            <div className="text-xs font-semibold text-atlas-accent mb-2 uppercase tracking-wide">AI Research Brief</div>
            <div className="text-sm text-atlas-heading whitespace-pre-wrap">{aiAnalysis}</div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        <div className="text-sm font-semibold text-atlas-heading">Research Notes</div>
        <div className="flex gap-2">
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="text-sm border border-gray-200 rounded px-2 py-1 text-atlas-description"
          >
            {["General", "Market", "Competitive", "Financial", "Operational"].map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <Input
            placeholder="Add a research note…"
            value={content}
            onChange={e => setContent(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addNote()}
            className="flex-1 text-sm border-gray-200"
          />
          <Button onClick={addNote} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm">Add</Button>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {notes.length === 0 && <div className="text-sm text-atlas-description">No notes yet. Add your first research note above.</div>}
          {notes.map(n => (
            <div key={n.id} className="bg-gray-50 border border-gray-100 rounded p-3">
              <div className="flex items-center justify-between mb-1">
                <Badge className="text-xs bg-gray-100 text-gray-600 border-gray-200">{n.category}</Badge>
                <span className="text-xs text-atlas-description">{new Date(n.created_at).toLocaleDateString()}</span>
              </div>
              <div className="text-sm text-atlas-heading">{n.content}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Identification() {
  const [targets, setTargets] = useState(getTargets);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [showForm, setShowForm] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [form, setForm] = useState({ name: "", type: "General", sector: "", geography: "", website: "", description: "", notes: "" });
  const [activeTab, setActiveTab] = useState("scoring");

  useEffect(() => { saveTargets(targets); }, [targets]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return targets.filter(t =>
      (!q || t.name.toLowerCase().includes(q) || (t.sector || "").toLowerCase().includes(q)) &&
      (typeFilter === "All" || t.type === typeFilter) &&
      (statusFilter === "All" || t.status === statusFilter)
    );
  }, [targets, search, typeFilter, statusFilter]);

  const addTarget = () => {
    if (!form.name.trim()) return;
    const obj = { id: crypto.randomUUID(), ...form, status: "Active", created_at: Date.now(), updated_at: Date.now() };
    setTargets(prev => [obj, ...prev]);
    setForm({ name: "", type: "General", sector: "", geography: "", website: "", description: "", notes: "" });
    setShowForm(false);
  };

  const updateStatus = (id, status) => {
    setTargets(prev => prev.map(t => t.id === id ? { ...t, status, updated_at: Date.now() } : t));
  };

  const deleteTarget = (id) => {
    setTargets(prev => prev.filter(t => t.id !== id));
  };

  const stats = useMemo(() => ({
    total: targets.length,
    general: targets.filter(t => t.type === "General").length,
    competitor: targets.filter(t => t.type === "Competitor").length,
    active: targets.filter(t => t.status === "Active").length,
  }), [targets]);

  return (
    <div className="space-y-6 p-6 bg-white min-h-screen">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-atlas-heading">Opportunities</h1>
        <p className="text-sm text-atlas-description mt-1">Deal scoring, competitor research & target identification</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 border border-gray-200">
          <TabsTrigger value="scoring" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">
            <Target size={14} className="mr-1" /> Deal Scoring
          </TabsTrigger>
          <TabsTrigger value="research" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">
            <Search size={14} className="mr-1" /> Research Targets
          </TabsTrigger>
        </TabsList>

        {/* Deal Scoring Tab */}
        <TabsContent value="scoring" className="mt-4">
          <DealScoring />
        </TabsContent>

        {/* Research Targets Tab */}
        <TabsContent value="research" className="space-y-4 mt-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Targets", value: stats.total, icon: <Globe size={18} className="text-atlas-accent" /> },
              { label: "General Research", value: stats.general, icon: <FileText size={18} className="text-purple-500" /> },
              { label: "Competitor Research", value: stats.competitor, icon: <TrendingUp size={18} className="text-orange-500" /> },
              { label: "Active", value: stats.active, icon: <Building2 size={18} className="text-emerald-500" /> },
            ].map((s, i) => (
              <Card key={i} className="border border-gray-100 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  {s.icon}
                  <div>
                    <div className="text-2xl font-bold text-atlas-heading">{s.value}</div>
                    <div className="text-xs text-atlas-description">{s.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filters */}
          <Card className="border border-gray-100 shadow-sm">
            <CardContent className="p-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-48">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-atlas-description" />
                <Input
                  placeholder="Search targets…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 border-gray-200 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-atlas-description">Type:</span>
                {["All", ...RESEARCH_TYPES].map(t => (
                  <button
                    key={t}
                    onClick={() => setTypeFilter(t)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${typeFilter === t ? "bg-atlas-accent text-white border-atlas-accent" : "bg-white text-atlas-description border-gray-200 hover:border-atlas-accent"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-atlas-description">Status:</span>
                {["All", ...STATUSES].map(s => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${statusFilter === s ? "bg-atlas-accent text-white border-atlas-accent" : "bg-white text-atlas-description border-gray-200 hover:border-atlas-accent"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Button onClick={() => setShowForm(true)} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm">
                <Plus size={14} className="mr-1" /> Add Target
              </Button>
            </CardContent>
          </Card>

          {/* Add Form */}
          {showForm && (
            <Card className="border border-blue-100 bg-blue-50 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-atlas-heading">Add Research Target</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-atlas-description mb-1 block">Company Name *</label>
                    <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Acme Corp" className="border-gray-200 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-atlas-description mb-1 block">Research Type</label>
                    <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="w-full text-sm border border-gray-200 rounded px-3 py-2 text-atlas-heading">
                      {RESEARCH_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-atlas-description mb-1 block">Sector</label>
                    <select value={form.sector} onChange={e => setForm(p => ({ ...p, sector: e.target.value }))} className="w-full text-sm border border-gray-200 rounded px-3 py-2 text-atlas-heading">
                      <option value="">Select sector…</option>
                      {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-atlas-description mb-1 block">Geography</label>
                    <Input value={form.geography} onChange={e => setForm(p => ({ ...p, geography: e.target.value }))} placeholder="e.g. North America" className="border-gray-200 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-atlas-description mb-1 block">Website</label>
                    <Input value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://…" className="border-gray-200 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-atlas-description mb-1 block">Description</label>
                  <textarea
                    value={form.description}
                    onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description of the company or research context…"
                    rows={2}
                    className="w-full text-sm border border-gray-200 rounded px-3 py-2 text-atlas-heading resize-none"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={addTarget} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm">Add Target</Button>
                  <Button onClick={() => setShowForm(false)} variant="outline" className="text-sm border-gray-200">Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Targets Table */}
          <Card className="border border-gray-100 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 border-b border-gray-100">
                      <TableHead className="text-xs font-semibold text-atlas-description uppercase tracking-wide">Company</TableHead>
                      <TableHead className="text-xs font-semibold text-atlas-description uppercase tracking-wide">Type</TableHead>
                      <TableHead className="text-xs font-semibold text-atlas-description uppercase tracking-wide">Sector</TableHead>
                      <TableHead className="text-xs font-semibold text-atlas-description uppercase tracking-wide">Geography</TableHead>
                      <TableHead className="text-xs font-semibold text-atlas-description uppercase tracking-wide">Status</TableHead>
                      <TableHead className="text-xs font-semibold text-atlas-description uppercase tracking-wide">Added</TableHead>
                      <TableHead className="text-xs font-semibold text-atlas-description uppercase tracking-wide">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-atlas-description text-sm py-8">
                          No research targets found. Add your first target above.
                        </TableCell>
                      </TableRow>
                    )}
                    {filtered.map(t => (
                      <TableRow key={t.id} className="hover:bg-gray-50 border-b border-gray-50">
                        <TableCell>
                          <button
                            onClick={() => setSelectedTarget(t)}
                            className="text-sm font-medium text-atlas-accent hover:underline text-left"
                          >
                            {t.name}
                          </button>
                          {t.description && <div className="text-xs text-atlas-description truncate max-w-xs">{t.description}</div>}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs border ${TYPE_COLORS[t.type] || "bg-gray-100 text-gray-600"}`}>{t.type}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-atlas-description">{t.sector || "—"}</TableCell>
                        <TableCell className="text-sm text-atlas-description">{t.geography || "—"}</TableCell>
                        <TableCell>
                          <select
                            value={t.status}
                            onChange={e => updateStatus(t.id, e.target.value)}
                            className={`text-xs border rounded px-2 py-1 ${STATUS_COLORS[t.status] || ""}`}
                          >
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </TableCell>
                        <TableCell className="text-xs text-atlas-description">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => setSelectedTarget(t)}
                              variant="outline"
                              className="h-7 text-xs border-gray-200 text-atlas-accent hover:bg-blue-50"
                            >
                              Open
                            </Button>
                            <button onClick={() => deleteTarget(t.id)} className="text-red-400 hover:text-red-600">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Research Workspace Dialog */}
      <Dialog open={!!selectedTarget} onOpenChange={() => setSelectedTarget(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-atlas-heading">Research Workspace</DialogTitle>
          </DialogHeader>
          {selectedTarget && <ResearchNotes target={selectedTarget} onClose={() => setSelectedTarget(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
